import React, { useState } from "react";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import type { IApiServiceError } from "store";
import type { SourceRow } from "store/types/news";

import type { FeedConfigBuckets } from "../../context/NewsFeedContext";
import { btnGhostSx, btnSx, fieldInputSx, fieldLabelSx, ibtnSx, sectionLabelSx } from "./styles";

const normalizeSourceUrl = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const { hostname } = new URL(withProtocol);
    return hostname.replace(/^www\./i, "");
  } catch {
    return trimmed;
  }
};

type Props = {
  initialSources: FeedConfigBuckets<SourceRow>;
  onCreateSource: (payload: SourceRow) => Promise<SourceRow>;
  onUpdateSourcePriority: (source_url: string, priority: number, name: string) => Promise<SourceRow>;
  onDeleteSource: (source_url: string) => Promise<void>;
  onSelectSystemSource: (source_url: string, name: string) => Promise<void>;
  onUnselectSystemSource: (source_url: string) => Promise<void>;
};

export const SourcesPanel: React.FC<Props> = ({
  initialSources,
  onCreateSource,
  onUpdateSourcePriority,
  onDeleteSource,
  onSelectSystemSource,
  onUnselectSystemSource
}) => {
  const [sources, setSources] = useState<FeedConfigBuckets<SourceRow>>(initialSources);
  const [manualUrl, setManualUrl] = useState("");
  const [manualName, setManualName] = useState("");
  const [createSourceError, setCreateSourceError] = useState<string | null>(null);

  const moveSourcePriority = async (source_url: string, dir: number) => {
    const arr = [...sources.user];
    const i = arr.findIndex((s) => s.source_url === source_url);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    const priorityA = j + 1;
    const priorityB = i + 1;
    const a = { ...arr[i], priority: priorityA };
    const b = { ...arr[j], priority: priorityB };
    arr[i] = b;
    arr[j] = a;
    setSources((curr) => ({ ...curr, user: arr }));
    await Promise.all([
      onUpdateSourcePriority(a.source_url, priorityA, a.name),
      onUpdateSourcePriority(b.source_url, priorityB, b.name)
    ]);
  };

  const removeSource = async (source_url: string) => {
    const target = sources.user.find((s) => s.source_url === source_url);
    if (!target) return;
    if (target.user_id == null) {
      await onUnselectSystemSource(source_url);
      setSources((curr) => ({
        system: [...curr.system, { ...target, priority: null }],
        user: curr.user.filter((s) => s.source_url !== source_url)
      }));
    } else {
      await onDeleteSource(source_url);
      setSources((curr) => ({
        ...curr,
        user: curr.user.filter((s) => s.source_url !== source_url)
      }));
    }
  };

  const addAvailableSource = async (source_url: string, name: string) => {
    const target = sources.system.find((s) => s.source_url === source_url);
    if (!target) return;
    await onSelectSystemSource(source_url, name);
    setSources((curr) => ({
      system: curr.system.filter((s) => s.source_url !== source_url),
      user: [...curr.user, { ...target, priority: curr.user.length + 1, name }]
    }));
  };

  const addManualSource = async () => {
    const url = normalizeSourceUrl(manualUrl);
    if (!url) return;
    const name = manualName.trim();
    setCreateSourceError(null);
    try {
      const created = await onCreateSource({
        user_id: null,
        name: name || url,
        source_url: url,
        priority: sources.user.length + 1
      });
      setManualUrl("");
      setManualName("");
      setSources((curr) => ({ ...curr, user: [...curr.user, created] }));
    } catch (error) {
      setCreateSourceError(Object.values((error as IApiServiceError).errors)[0][0] || "Something went wrong");
    }
  };

  return (
    <Box>
      <Typography sx={sectionLabelSx}>Active sources</Typography>
      {sources.user.map((s, i) => (
        <Box
          key={s.source_url}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            py: "7px",
            borderBottom: "1px solid rgba(219,232,245,0.5)",
            "&:last-of-type": { borderBottom: "none" }
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#071f35" }}>{s.name}</Typography>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  height: 18,
                  alignItems: "center",
                  borderRadius: "100px",
                  px: "6px",
                  bgcolor: "#f0f2f5",
                  color: "#5d7782",
                  fontSize: 10,
                  fontWeight: 700
                }}
              >
                Priority {i + 1}
              </Box>
            </Box>
            <Typography sx={{ fontSize: 10, color: "#797979", mt: "2px" }}>{s.source_url}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "3px", flexShrink: 0, alignItems: "center" }}>
            <IconButton disabled={i === 0} onClick={() => moveSourcePriority(s.source_url, -1)} sx={ibtnSx}>
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              disabled={i === sources.user.length - 1}
              onClick={() => moveSourcePriority(s.source_url, 1)}
              sx={ibtnSx}
            >
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Button
              onClick={() => removeSource(s.source_url)}
              sx={{
                ...btnGhostSx,
                color: "#d32f2f",
                px: "7px",
                "&:hover": { bgcolor: "#f6f8fc", color: "#d32f2f", borderColor: "transparent" }
              }}
            >
              Remove
            </Button>
          </Box>
        </Box>
      ))}

      {sources.system.length ? (
        <>
          <Typography sx={{ ...sectionLabelSx, mt: "14px" }}>Available sources</Typography>
          {sources.system.map((s, i) => (
            <Box
              key={s.source_url}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
                py: "7px",
                borderBottom: i < sources.system.length - 1 ? "1px solid rgba(219,232,245,0.5)" : "none"
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#071f35" }}>{s.name}</Typography>
                <Typography sx={{ fontSize: 10, color: "#797979", mt: "2px" }}>{s.source_url}</Typography>
              </Box>
              <Button
                onClick={() => addAvailableSource(s.source_url, s.name)}
                sx={{ ...btnSx, height: 26, fontSize: 11 }}
              >
                Add
              </Button>
            </Box>
          ))}
        </>
      ) : null}

      <Typography sx={{ ...sectionLabelSx, mt: "14px" }}>Add custom source</Typography>
      <Box sx={{ mb: "10px" }}>
        <Typography component="label" htmlFor="nai-custom-src-url" sx={fieldLabelSx}>
          Source URL
        </Typography>
        <TextField
          id="nai-custom-src-url"
          fullWidth
          hiddenLabel
          variant="outlined"
          placeholder="https://example.com"
          value={manualUrl}
          onChange={(e) => {
            setCreateSourceError(null);
            setManualUrl(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") addManualSource();
          }}
          sx={fieldInputSx}
        />
      </Box>
      <Box sx={{ mb: "10px" }}>
        <Typography component="label" htmlFor="nai-custom-src-name" sx={fieldLabelSx}>
          Display name (optional)
        </Typography>
        <TextField
          id="nai-custom-src-name"
          fullWidth
          hiddenLabel
          variant="outlined"
          placeholder="Name"
          value={manualName}
          onChange={(e) => {
            setCreateSourceError(null);
            setManualName(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") addManualSource();
          }}
          sx={fieldInputSx}
        />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "6px" }}>
        <Button onClick={addManualSource} sx={btnSx}>
          Add source
        </Button>
        {createSourceError ? (
          <Typography component="p" role="alert" sx={{ fontSize: 12, color: "#d32f2f", lineHeight: 1.45, m: 0 }}>
            {createSourceError}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
